import type { FC } from "react";
import {
  PersonifySsoControllerService,
  TranscarentSsoControllerService
} from "@9amhealth/openapi";
import type {
  CancelablePromise,
  SuccessLoginResponse
} from "@9amhealth/openapi";
import { AuthenticationMethod } from "src/constants/authenticationMethod";
import { PayerId } from "src/constants/payers";
import FunnelKey from "src/constants/funnelKey";
import { KnownPartnerSources } from "src/constants/partner";
import { IconPersonifyHealth, IconTranscarent } from "src/constants/icons";
import type { KnownDynamicFlags } from "src/lib/featureFlags";
import { getContinueWithTranscarentTargetUrl } from "src/lib/getContinueWithTranscarentTargetUrl";
import { getContinueWithPersonifyTargetUrl } from "src/lib/getContinueWithPersonifyTargetUrl";

export interface PartnerConfig {
  payerId: PayerId;
  funnelKey: FunnelKey;
  /** The auth method a real SSO session from this partner carries. */
  ssoAuthMethod: AuthenticationMethod;
  /** Session-auth methods that satisfy this partner's payer-gated reauth check. */
  acceptedAuthMethods: AuthenticationMethod[];
  /**
   * True when 9am is loaded inside the partner's own in-app webview (e.g.
   * Transcarent's `TCMobileInAppBrowser`), which drives webview-chrome UI
   * changes (tab-style nav, hidden chrome). Personify is NOT embedded — the
   * member reaches 9am as a normal browser/app session.
   */
  isEmbeddedWebview: boolean;
  /** Only set when isEmbeddedWebview — substring matched against navigator.userAgent. */
  embeddedWebviewUserAgentSubstring?: string;
  /** Matched against the `?from=` query param on landing. */
  detectQueryParam?: string;
  /** Additive `html` class (alongside `partner-session`) that scopes this partner's brand CSS vars. */
  cssClassName: string;
  icon: FC;
  /** i18next context suffix for `login`/`login.subtitle` translation keys. */
  loginTranslateContext: string;
  ssoLoginFeatureFlag: KnownDynamicFlags;
  getContinueUrl: () => string | undefined;
  /**
   * The JWT `type` claim this partner's single-use SSO exchange token carries
   * (backend `JwtTokenClaims.TokenType`, e.g. `TRANSCARENT_SSO_EXCHANGE`).
   * `HandleOneTimeToken` decodes an incoming `/auth/ott` token and looks up
   * its partner by this claim, since both partners share that one landing
   * route and the token itself is the only signal available to tell them
   * apart (see `getPartnerConfigByExchangeTokenType`).
   */
  exchangeTokenType: string;
  /** Exchanges this partner's single-use SSO token for a 9am session. */
  exchangeToken: (token: string) => CancelablePromise<SuccessLoginResponse>;
}

export const PARTNER_CONFIGS: Partial<
  Record<KnownPartnerSources, PartnerConfig>
> = {
  [KnownPartnerSources.transcarent]: {
    payerId: PayerId.TRANSCARENT,
    funnelKey: FunnelKey.transcarent,
    ssoAuthMethod: AuthenticationMethod.TRANSCARENT_SSO,
    acceptedAuthMethods: [
      AuthenticationMethod.SAML2,
      AuthenticationMethod.TRANSCARENT_SSO
    ],
    isEmbeddedWebview: true,
    embeddedWebviewUserAgentSubstring: "TCMobileInAppBrowser",
    detectQueryParam: "tc-ott",
    cssClassName: "partner-source-transcarent",
    icon: IconTranscarent,
    loginTranslateContext: "transcarent",
    ssoLoginFeatureFlag: "sso_login_option_transcarent",
    getContinueUrl: getContinueWithTranscarentTargetUrl,
    exchangeTokenType: "TRANSCARENT_SSO_EXCHANGE",
    exchangeToken: (token) =>
      TranscarentSsoControllerService.exchangeToken({ token })
  },
  [KnownPartnerSources.personifyHealth]: {
    payerId: PayerId.PERSONIFY_HEALTH,
    funnelKey: FunnelKey.personifyHealth,
    ssoAuthMethod: AuthenticationMethod.PERSONIFY_SSO,
    acceptedAuthMethods: [AuthenticationMethod.PERSONIFY_SSO],
    // Personify does not embed 9am in a webview — no UA substring, so
    // detection never sets webview-chrome UI (mobileTabNavigation) for it.
    isEmbeddedWebview: false,
    detectQueryParam: "ph-ott",
    cssClassName: "partner-source-personify-health",
    icon: IconPersonifyHealth,
    loginTranslateContext: "personify_health",
    ssoLoginFeatureFlag: "sso_login_option_personify_health",
    getContinueUrl: getContinueWithPersonifyTargetUrl,
    exchangeTokenType: "PERSONIFY_SSO_EXCHANGE",
    exchangeToken: (token) =>
      PersonifySsoControllerService.exchangePersonifySsoToken({ token })
  }
};

/**
 * Finds the partner whose SSO exchange token carries the given JWT `type`
 * claim. `/auth/ott` is shared by every partner's exchange-token handover
 * (backend redirects there with just `?token=`, no partner marker), so the
 * token's own declared type is the only reliable way to route it — see
 * `PartnerConfig.exchangeTokenType`.
 */
export const getPartnerConfigByExchangeTokenType = (
  tokenType?: string
): PartnerConfig | undefined =>
  Object.values(PARTNER_CONFIGS).find(
    (config) => config.exchangeTokenType === tokenType
  );

/**
 * True if `method` is a partner's own dedicated SSO auth method (e.g.
 * TRANSCARENT_SSO, PERSONIFY_SSO) — NOT the generic SAML2 method, even though
 * SAML2 also satisfies some partners' `acceptedAuthMethods`. Used for
 * identity-field locking, which is only appropriate when the SSO exchange
 * itself supplied the identity data (the dedicated OTT/exchange flows), not
 * for a generic SAML2 login where 9am has no such guarantee.
 */
export const isPartnerPrimarySsoMethod = (method?: string): boolean =>
  Object.values(PARTNER_CONFIGS).some(
    (config) => config.ssoAuthMethod === method
  );

/**
 * True if `method` satisfies ANY partner's accepted-auth-methods list
 * (includes generic SAML2 where a partner accepts it). Used where the caller
 * only cares "is this session already SSO-confirmed by some partner",
 * regardless of which one.
 */
export const isAnyPartnerAcceptedAuthMethod = (method?: string): boolean =>
  Object.values(PARTNER_CONFIGS).some((config) =>
    config.acceptedAuthMethods.includes(method as AuthenticationMethod)
  );

/** Finds the partner config whose `payerId` matches, if any. */
export const getPartnerConfigByPayerId = (
  payerId?: string
): PartnerConfig | undefined =>
  Object.values(PARTNER_CONFIGS).find((config) => config.payerId === payerId);

/** Finds the partner key whose `payerId` matches, if any. */
export const getPartnerSourceByPayerId = (
  payerId?: string
): KnownPartnerSources | undefined => {
  const entry = Object.entries(PARTNER_CONFIGS).find(
    ([, config]) => config.payerId === payerId
  );
  return entry?.[0] as KnownPartnerSources | undefined;
};
